import LineIcon from "@assets/images/line.svg";
import RefreshIcon from "@assets/images/refresh-primary.svg";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  GridSize,
  Radio,
  RadioGroup
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DatePicker from "@saleor/components/DatePicker";
import { FormSpacer } from "@saleor/components/FormSpacer";
import MultipleAutocompleteTextInput from "@saleor/components/MultipleAutocompleteTextInput";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import {
  ExportSoldTosQuery,
  FilterSoldToDomesticOrderQuery
} from "@saleor/graphql";
import useOrderFilterSalesOrderList from "@saleor/hooks/useOrderFilterListSalesOrder";
import InputSuggestion from "@saleor/listOfSalesOrder/components/InputSuggestionSalesOrder";
import { mapEdgesToItems } from "@saleor/utils/maps";
import classNames from "classnames";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import { OrderListFilterProps } from "../OrderListFilter";
import { FIELD_TYPE } from "./constant";
import useStyles from "./styles";

const OrderFilterSection: React.FC<OrderListFilterProps> = ({
  handleChangeFilter,
  onResetForm,
  message,
  listOrder,
  shouldSetDefaultCreatedBy,
  loading
}) => {
  const classes = useStyles();
  const [reRender, setRender] = useState<boolean>(false);

  const {
    formik,
    isOpen,
    setIsOpen,
    isClean,
    handleCleanForm,
    formData,
    ArrowIconComponent,
    getSuggestionSoldTos,
    fetchSuggestionSoldTos,
    loadingSuggestionSoldTos,
    getRequireAttentionSaleEmployee,
    fetchRequireAttentionSaleEmployee,
    loadingRequireAttentionSaleEmployee,
    getRequireAttentionMaterial,
    fetchRequireAttentionMaterial,
    loadingRequireAttentionMaterial,
    getCreateBySaleOrder,
    fetchCreateBySaleOrder,
    loadingCreateBySaleOrder,
    scgpMe
  } = useOrderFilterSalesOrderList({
    handleChangeFilter,
    shouldSetDefaultCreatedBy
  });

  useEffect(() => {
    if (
      scgpMe?.scgpMe?.id &&
      formik?.setFieldValue &&
      shouldSetDefaultCreatedBy
    ) {
      formik.setFieldValue("createBy", scgpMe.scgpMe.id);
    }
  }, [scgpMe, formik?.setFieldValue]);

  useEffect(() => {
    if (listOrder.length > 0) {
      setIsOpen(false);
    }
  }, [listOrder]);

  const getTextError = field => {
    if (!formik.errors[field]?.lte && !formik.errors[field]?.gte) {
      return <div className={classes.textError}>{formik.errors[field]}</div>;
    }
  };

  const getTextErrorDatePick = field => {
    if (formik.errors[field]?.lte) {
      return (
        <div className={classes.textError}>{formik.errors[field]?.lte}</div>
      );
    }
    if (formik.errors[field]?.gte) {
      return (
        <div className={classes.textError}>{formik.errors[field]?.gte}</div>
      );
    }
  };

  const isDisabled = useMemo(() => {
    if (
      !formik.values.saleOrg &&
      !formik.values.purchaseOrderNo &&
      !formik.values.saleOrderNo
    ) {
      return true;
    }

    if (
      !(formik.values.createDate.gte && formik.values.createDate.lte) &&
      !(
        formik.values.requestDeliveryDate.gte &&
        formik.values.requestDeliveryDate.lte
      ) &&
      !formik.values.purchaseOrderNo &&
      !formik.values.saleOrderNo
    ) {
      return true;
    }

    return !formik.dirty;
  }, [
    formik?.values?.saleOrg,
    formik?.values?.createDate?.gte,
    formik?.values?.createDate?.lte,
    formik?.values?.requestDeliveryDate?.gte,
    formik?.values?.requestDeliveryDate?.lte,
    formik?.values?.purchaseOrderNo,
    formik?.values?.saleOrderNo,
    formik?.dirty
  ]);

  const handleResetForm = () => {
    setRender(prevState => !prevState);
    handleCleanForm();
    onResetForm();
  };

  const mappingDataSuggest = name => {
    if (name === "salesEmployeeNo") {
      return {
        fetchDataSuggestions: fetchRequireAttentionSaleEmployee,
        loadingDataSuggestion: loadingRequireAttentionSaleEmployee,
        getDataSuggestions: getRequireAttentionSaleEmployee,
        searchField: "saleEmployee",
        dataOptions:
          mapEdgesToItems(
            fetchRequireAttentionSaleEmployee?.filterRequireAttentionSaleEmployee
          )?.map(data => ({
            title: `${data.code} - ${data.name}`,
            value: `${data.code} - ${data.name}`
          })) || []
      };
    }

    if (name === "createBy") {
      return {
        fetchDataSuggestions: fetchCreateBySaleOrder,
        loadingDataSuggestion: loadingCreateBySaleOrder,
        getDataSuggestions: getCreateBySaleOrder,
        searchField: "search",
        dataOptions:
          mapEdgesToItems(
            fetchCreateBySaleOrder?.suggestionSearchCreateBySaleOrder
          )?.map(data => ({
            title: `${data.firstName} - ${data.lastName}`,
            value: `${data.userId}`
          })) || []
      };
    }
  };

  const mappingDataSuggestMutipleSelect = name => {
    if (name === "soldTo") {
      return {
        fetchDataSuggestions: fetchSuggestionSoldTos,
        loadingDataSuggestion: loadingSuggestionSoldTos,
        getDataSuggestions: getSuggestionSoldTos,
        searchField: "soldTo",
        dataOptions:
          mapEdgesToItems(
            isSuggestionSoldTo(fetchSuggestionSoldTos)
              ? fetchSuggestionSoldTos?.filterSoldToDomesticOrder
              : fetchSuggestionSoldTos?.exportSoldTos
          )?.map(data => ({
            title: `${data.code} - ${data.name}`,
            value: data.code
          })) || []
      };
    }

    if (name === "materialNoMaterialDescription") {
      return {
        fetchDataSuggestions: fetchRequireAttentionMaterial,
        loadingDataSuggestion: loadingRequireAttentionMaterial,
        getDataSuggestions: getRequireAttentionMaterial,
        searchField: "material",
        isCheckingRole: true,
        dataOptions:
          mapEdgesToItems(
            fetchRequireAttentionMaterial?.filterRequireAttentionMaterial
          )?.map(data => ({
            title: `${data.code} - ${data.descriptionEn}`,
            value: data.code
          })) || []
      };
    }
  };

  const createdAndRequestedDateRange = useMemo(() => {
    const { soldTo = [], materialNoMaterialDescription = [] } =
      formik.values ?? {};
    const soldToCount = soldTo.length;
    const materialCount = materialNoMaterialDescription.length;

    if (soldToCount === 1 || (soldToCount === 0 && materialCount === 1)) {
      return 91;
    }
    return 31;
  }, [formik.values.soldTo, formik.values.materialNoMaterialDescription]);

  useEffect(() => {
    const { gte: createDateStart, lte: createDateEnd } =
      formik.values.createDate ?? {};
    const { gte: requestDateStart, lte: requestDateEnd } =
      formik.values.requestDeliveryDate ?? {};
    const maxDiff = createdAndRequestedDateRange - 1;

    if (createDateStart && createDateEnd) {
      const diffDays = moment(createDateStart).diff(
        moment(createDateEnd),
        "days"
      );
      if (Math.abs(diffDays) > maxDiff) {
        formik.setFieldValue("createDate", { gte: "", lte: "" });
      }
    }

    if (requestDateStart && requestDateEnd) {
      const diffDays = moment(requestDateStart).diff(
        moment(requestDateEnd),
        "days"
      );
      if (Math.abs(diffDays) > maxDiff) {
        formik.setFieldValue("requestDeliveryDate", { gte: "", lte: "" });
      }
    }
  }, [createdAndRequestedDateRange]);

  useEffect(() => {
    if (formik.values.createDate.lte) {
      const maxDiff = createdAndRequestedDateRange - 1;

      const diff = moment(formik.values.createDate.lte).diff(
        moment(formik.values.createDate.gte),
        "days"
      );
      if (Math.abs(diff) > maxDiff) {
        const newLte = moment(formik.values.createDate.gte).add(maxDiff, "day");
        formik.setFieldValue("createDate.lte", newLte);
      }
    }
  }, [formik?.values?.createDate?.gte]);

  useEffect(() => {
    if (formik.values.createDate.gte) {
      const maxDiff = createdAndRequestedDateRange - 1;

      const diff = moment(formik.values.createDate.gte).diff(
        moment(formik.values.createDate.lte),
        "days"
      );
      if (Math.abs(diff) > maxDiff) {
        const newGte = moment(formik.values.createDate.lte).subtract(
          maxDiff,
          "day"
        );
        formik.setFieldValue("createDate.gte", newGte);
      }
    } else {
      formik.setFieldValue("createDate.gte", null);
    }
  }, [formik?.values?.createDate?.lte]);

  useEffect(() => {
    if (formik.values.requestDeliveryDate.lte) {
      const maxDiff = createdAndRequestedDateRange - 1;

      const diff = moment(formik.values.requestDeliveryDate.lte).diff(
        moment(formik.values.requestDeliveryDate.gte),
        "days"
      );
      if (Math.abs(diff) > maxDiff) {
        const newLte = moment(formik.values.requestDeliveryDate.gte).add(
          maxDiff,
          "day"
        );
        formik.setFieldValue("requestDeliveryDate.lte", newLte);
      }
    }
  }, [formik?.values?.requestDeliveryDate?.gte]);

  useEffect(() => {
    if (formik.values.requestDeliveryDate.gte) {
      const maxDiff = createdAndRequestedDateRange - 1;

      const diff = moment(formik.values.requestDeliveryDate.gte).diff(
        moment(formik.values.requestDeliveryDate.lte),
        "days"
      );
      if (Math.abs(diff) > maxDiff) {
        const newGte = moment(formik.values.requestDeliveryDate.lte).subtract(
          maxDiff,
          "day"
        );
        formik.setFieldValue("requestDeliveryDate.gte", newGte);
      }
    }
  }, [formik?.values?.requestDeliveryDate?.lte]);

  const handleChangeDate = (e, child, type) => {
    formik.handleChange({
      target: {
        name: `${child.name}.${type}`,
        value: e.target.value
      }
    });
  };

  const renderItem = (
    <Grid className={classes.searchBox} container spacing={3}>
      {formData.map((child, key) => {
        const renderChild = (children: JSX.Element) => (
          <Grid
            item
            md={child.md ? (child.md as GridSize) : 2}
            sm={child.sm ? (child.sm as GridSize) : 6}
            key={key}
          >
            <div
              className={classNames({
                [classes.datePicker]: child.type === FIELD_TYPE.DATEPICKER
              })}
            >
              <MyTypography variant={"subtitle3"}>
                {child.title}
                {child.required && <span className={classes.required}>*</span>}
                {child.subTitle && (
                  <span
                    className={classes.subTitle}
                  >{` ${child.subTitle}`}</span>
                )}
              </MyTypography>
              {children}
              {getTextError(child.name)}
            </div>
          </Grid>
        );
        if (child.type === FIELD_TYPE.SINGLE_INPUT) {
          return renderChild(
            <SingleTextField
              name={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              maxLengthProps={child?.maxLength ? child.maxLength : undefined}
              id={child.name}
              testId={child.name}
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_SELECT) {
          return renderChild(
            <SingleSelectField
              className={classes.selectField}
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              choices={child.options}
              name={child.name}
              id={child.name}
              testId={child.name}
              InputProps={{
                inputProps: { "data-testid": `${child.name}-input` },
                placeholder: child.placeholder
              }}
              onChange={formik.handleChange}
              value={formik.values[child.name]}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              disabled={child?.disabled}
            />
          );
        }

        if (child.type === FIELD_TYPE.MULTIPLE_SELECT) {
          return renderChild(
            <SingleSelectField
              className={classes.selectField}
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              choices={child.options}
              name={child.name}
              id={child.name}
              testId={child.name}
              onChange={formik.handleChange}
              value={formik.values[child.name]}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
            />
          );
        }

        if (child.type === FIELD_TYPE.SUGGESTION_INPUT) {
          const dataSuggest = mappingDataSuggest(child.name);
          return renderChild(
            <InputSuggestion
              {...dataSuggest}
              contained
              key={`input${key}`}
              totalCount={dataSuggest?.dataOptions?.length}
              name={child.name}
              id={child.name}
              testId={child.name}
              defaultInputValue={
                child.name === "createBy"
                  ? formik.values.defaultInputSearchCreateBy
                  : formik.values[child.name]
              }
              defaultValue={{
                title:
                  child.name === "createBy"
                    ? formik.values.defaultInputSearchCreateBy
                    : formik.values[child.name],
                value: formik.values[child.name]
              }}
              objectSuggestion
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              isClean={isClean}
              handleSelectedSearch={e => {
                formik.handleChange({
                  target: {
                    name: child.name,
                    value: typeof e === "string" ? e : e.value
                  }
                });
              }}
            />
          );
        }

        if (child.type === FIELD_TYPE.MULTIPLE_SUGGESTION_INPUT) {
          const dataSuggest = mappingDataSuggestMutipleSelect(child.name);

          if (child.name === "soldTo") {
            return renderChild(
              <MultipleAutocompleteTextInput
                fullWidthItem
                reRender={reRender}
                headerText={child.headerText}
                message={child.message}
                keyFilterApi={child.keyFilterApi}
                name={child.name}
                id={child.name}
                testId={child.name}
                maxCount={child.maxCount}
                placeholder={child.placeholder}
                value={formik.values[child.name]}
                {...dataSuggest}
                handleSelectedSearch={value => {
                  formik.handleChange({
                    target: {
                      name: child.name,
                      value
                    }
                  });
                }}
                isIncludeOneTimeCustomer={true}
              />
            );
          } else {
            return renderChild(
              <MultipleAutocompleteTextInput
                fullWidthItem
                getByValue
                reRender={reRender}
                headerText={child.headerText}
                message={child.message}
                keyFilterApi={child.keyFilterApi}
                name={child.name}
                id={child.name}
                testId={child.name}
                maxCount={child.maxCount}
                placeholder={child.placeholder}
                {...dataSuggest}
                handleSelectedSearch={value => {
                  formik.handleChange({
                    target: {
                      name: child.name,
                      value
                    }
                  });
                }}
              />
            );
          }
        }

        if (child.type === FIELD_TYPE.RADIO_GROUP) {
          return renderChild(
            <RadioGroup
              className={classes.radioGroup}
              aria-labelledby="demo-controlled-radio-buttons-group"
              name={child.name}
              id={child.name}
              data-testid={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
            >
              {child.options?.map((o, oKey) => (
                <FormControlLabel
                  key={`radio${oKey}`}
                  value={o.value}
                  control={<Radio />}
                  label={
                    <MyTypography variant={"body3"}>{o.label}</MyTypography>
                  }
                />
              ))}
            </RadioGroup>
          );
        }

        if (child.type === FIELD_TYPE.DATEPICKER) {
          return renderChild(
            <>
              <Box display="flex" justifyItems="center" alignItems="center">
                <DatePicker
                  type="gte"
                  dateRange={createdAndRequestedDateRange}
                  name={`${child.name}`}
                  id={`${child.name}-from`}
                  data-testid={`${child.name}-from`}
                  disableManualInput
                  value={formik.values[child.name]?.gte || ""}
                  onChange={e => handleChangeDate(e, child, "gte")}
                  error={!!formik.errors[child.name]?.gte}
                  format="YYYY-MM-DD"
                  maxDate={
                    formik.values[child.name].lte
                      ? moment(formik.values[child.name].lte)
                      : undefined
                  }
                  minDate={undefined}
                  noBlock
                />
                <Box px={1} display="flex" alignItems="center">
                  <SVG src={LineIcon} width={12} height={2} />
                </Box>
                <DatePicker
                  type="lte"
                  dateRange={createdAndRequestedDateRange}
                  name={`${child.name}`}
                  id={`${child.name}-to`}
                  data-testid={`${child.name}-to`}
                  disableManualInput
                  value={formik.values[child.name]?.lte || ""}
                  error={!!formik.errors[child.name]?.lte}
                  onChange={e => {
                    handleChangeDate(e, child, "lte");
                  }}
                  format="YYYY-MM-DD"
                  maxDate={undefined}
                  minDate={
                    formik.values[child.name].gte
                      ? moment(formik.values[child.name].gte)
                      : undefined
                  }
                  noBlock
                />
              </Box>
              <Box className={classes.wrapDatePickerError}>
                <Box className={classes.datePickerError}>
                  {getTextErrorDatePick(child.name)}
                </Box>
                <Box className={classes.empty}></Box>
                <Box className={classes.datePickerError}>
                  {getTextErrorDatePick(child.name)}
                </Box>
              </Box>
            </>
          );
        }
      })}
    </Grid>
  );

  return (
    <>
      <Grid
        className={classes.header}
        container
        onClick={() => setIsOpen(!isOpen)}
      >
        <MyTypography variant="h6">
          <FormattedMessage defaultMessage="ค้นหา Sale Order" />
        </MyTypography>
        <ArrowIconComponent className={classes.arrowIcon} />
      </Grid>
      <Collapse in={isOpen}>
        {renderItem}
        <Box className={classes.divider}>
          <FormSpacer />
          <Divider />
        </Box>
        <Box className={classes.bottomBar} ml={3}>
          {message}
          <Box className={classes.flex}>
            <Box mr={2}>
              <Button
                className={classes.resetButton}
                variant={"outlined"}
                color={"secondary"}
                disabled={loading}
                onClick={handleResetForm}
                type="reset"
                id="resetForm"
                name="resetForm"
              >
                <SVG
                  src={RefreshIcon}
                  className={classes.icon}
                  width={16}
                  height={16}
                />
                <FormattedMessage defaultMessage="ล้างข้อมูล" />
              </Button>
            </Box>
            <Box>
              <Button
                className={classes.actionButton}
                variant={"contained"}
                disabled={isDisabled || loading}
                color={"secondary"}
                onClick={formik.submitForm}
                id="submitForm"
                name="submitForm"
              >
                <SearchIcon style={{ fontSize: "18px" }} />
                <FormattedMessage defaultMessage="ค้นหา" />
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

const isSuggestionSoldTo = (
  suggestionSoldTo: FilterSoldToDomesticOrderQuery | ExportSoldTosQuery | null
): suggestionSoldTo is FilterSoldToDomesticOrderQuery =>
  suggestionSoldTo
    ? Object.keys(suggestionSoldTo).includes("filterSoldToDomesticOrder")
    : null;

export default OrderFilterSection;
